/**
 * The app's own confirmation dialog.
 *
 * `window.confirm` blocks the whole tab, renders in the browser's chrome
 * rather than the platform's, cannot show a figure in the app's mono face,
 * and on a page that polls every four seconds it freezes the data behind it.
 * For a screen that cancels live orders that is the wrong instrument.
 *
 * Usage keeps the call site to one line:
 *
 *     const { ask, dialog } = useConfirm();
 *     <Button onClick={() => ask({ title: 'Stop PSO?', body: '…' }, run)}>Stop</Button>
 *     {dialog}
 *
 * The action runs only on confirm, so a caller cannot forget to check.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Button } from './UI';

export interface ConfirmRequest {
  title: string;
  /** Paragraphs. Say what will happen, not "are you sure?". */
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** 'down' for anything destructive — it colours the confirming button. */
  tone?: 'down' | 'up' | 'primary';
}

interface Pending extends ConfirmRequest {
  run: () => void;
}

export const useConfirm = () => {
  const [pending, setPending] = useState<Pending | null>(null);

  const ask = useCallback(
    (request: ConfirmRequest, run: () => void) => setPending({ ...request, run }),
    [],
  );

  const dialog = pending ? (
    <ConfirmDialog
      request={pending}
      onCancel={() => setPending(null)}
      onConfirm={() => {
        setPending(null);
        pending.run();
      }}
    />
  ) : null;

  return { ask, dialog };
};

const ConfirmDialog = ({
  request,
  onCancel,
  onConfirm,
}: {
  request: ConfirmRequest;
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  const cancelRef = useRef<HTMLButtonElement>(null);

  /**
   * Focus lands on CANCEL, not on the destructive button. Someone who hit the
   * wrong control once is the person this dialog exists for, and a stray Enter
   * or Space must not carry them straight through it.
   */
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
      // Enter confirms only from the confirming button, which does not have
      // focus to begin with — so it is always a deliberate second action.
      if (e.key === 'Tab') {
        // Two buttons, so the trap is just "keep it inside": let the browser
        // move between them and pull focus back if it escapes the dialog.
        requestAnimationFrame(() => {
          const root = document.getElementById('tk-confirm');
          if (root && !root.contains(document.activeElement)) cancelRef.current?.focus();
        });
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const tone = request.tone ?? 'down';
  return (
    <div
      onMouseDown={(e) => {
        // Only a press that both starts and ends on the backdrop dismisses it,
        // so a drag that happens to finish outside the card does not.
        if (e.target === e.currentTarget) onCancel();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        background: 'rgba(0, 0, 0, 0.55)',
        backdropFilter: 'blur(2px)',
        animation: 'tk-fade-in 0.12s var(--tk-ease)',
      }}
    >
      <div
        id="tk-confirm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tk-confirm-title"
        style={{
          width: 'min(440px, 100%)',
          background: 'var(--tk-surface)',
          border: '1px solid var(--tk-line)',
          borderRadius: 'var(--tk-radius-lg)',
          boxShadow: 'var(--tk-shadow-lg, 0 24px 60px rgba(0,0,0,0.45))',
          padding: 24,
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        <h2
          id="tk-confirm-title"
          style={{
            margin: 0,
            fontSize: 17,
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: 'var(--tk-ink)',
          }}
        >
          {request.title}
        </h2>
        {request.body ? (
          <div
            style={{
              fontSize: 13.5,
              lineHeight: 1.55,
              color: 'var(--tk-ink-2)',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            {request.body}
          </div>
        ) : null}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
          <Button ref={cancelRef} variant="ghost" onClick={onCancel}>
            {request.cancelLabel ?? 'Cancel'}
          </Button>
          <Button variant={tone} onClick={onConfirm}>
            {request.confirmLabel ?? 'Confirm'}
          </Button>
        </div>
      </div>
    </div>
  );
};
